import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Send, Copy, CheckCircle2, Clock, XCircle, RefreshCw, Users } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { client_name: "", client_email: "", client_phone: "", company: "", plan_interested: "", note: "" };
const STATUS_STYLE = {
  PENDING: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  APPROVED: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  REJECTED: "text-red-400 border-red-500/40 bg-red-500/10",
};

export default function PartnerDashboard() {
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.get("/partner/me"), api.get("/partner/referrals")]);
      setMe(m.data);
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/partner/login");
      else setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/membership/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const set = (k, target) => (e) => target({ ...(target === setForm ? form : editForm), [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setNotice("");
    try {
      await api.post("/partner/referrals", form);
      setForm(EMPTY);
      setNotice("Referral submitted — now under admin review.");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.put(`/partner/referrals/${editTarget.referral_id}`, editForm);
      setEditTarget(null);
      setNotice("Referral updated and resubmitted for review.");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (me?.referral_link) {
      navigator.clipboard.writeText(me.referral_link).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  if (!me) return <div className="section-dark min-h-screen flex items-center justify-center" data-testid="partner-dashboard-loading"><Loader2 size={24} className="animate-spin text-gold" /></div>;
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0, total: 0 };

  return (
    <section className="section-dark min-h-screen py-10 px-4 md:px-8" data-testid="partner-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="gold-kicker mb-1">PARTNER DASHBOARD</p>
            <h1 className="font-display text-2xl md:text-3xl font-black text-slate-100" data-testid="partner-name">{me.full_name}</h1>
            <p className="text-xs font-mono text-slate-500 mt-1" data-testid="partner-id">{me.partner_id} • {me.email}</p>
          </div>
          <button onClick={async () => { await api.post("/partner/logout").catch(() => {}); navigate("/partner/login"); }}
            data-testid="partner-logout-btn" className="btn-outline text-xs"><LogOut size={14} /> LOGOUT</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="referral-stats">
          {[["PENDING", counts.pending, Clock, "stat-pending"], ["APPROVED", counts.approved, CheckCircle2, "stat-approved"],
            ["REJECTED", counts.rejected, XCircle, "stat-rejected"], ["TOTAL REFERRALS", counts.total, Users, "stat-total"]].map(([label, val, Icon, tid]) => (
            <div key={tid} className="card-surface p-5" data-testid={tid}>
              <div className="flex items-center justify-between"><p className="text-2xl font-bold font-mono text-slate-100">{val}</p><Icon size={16} className="text-gold" /></div>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
            </div>
          ))}
        </div>

        {me.referral_link && (
          <div className="card-surface p-4 mb-8 flex flex-wrap items-center gap-3" data-testid="referral-link-card">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Your Referral Link</p>
            <code className="text-xs text-gold flex-1 min-w-0 truncate" data-testid="referral-link-text">{me.referral_link}</code>
            <button onClick={copyLink} data-testid="copy-referral-link-btn" className="btn-outline !px-3 !py-1.5 text-[10px]">
              {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />} {copied ? "COPIED" : "COPY"}
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          <form onSubmit={submit} className="card-surface p-6 space-y-4 lg:col-span-2 self-start" data-testid="referral-form">
            <h2 className="font-display text-lg font-bold text-slate-100">Refer a Client</h2>
            <input value={form.client_name} onChange={set("client_name", setForm)} placeholder="Client Name *" data-testid="ref-client-name" className="input-dark text-sm w-full" required />
            <input type="email" value={form.client_email} onChange={set("client_email", setForm)} placeholder="Client Email *" data-testid="ref-client-email" className="input-dark text-sm w-full" required />
            <input value={form.client_phone} onChange={set("client_phone", setForm)} placeholder="Client Phone" data-testid="ref-client-phone" className="input-dark text-sm w-full" />
            <input value={form.company} onChange={set("company", setForm)} placeholder="Client Company *" data-testid="ref-company" className="input-dark text-sm w-full" required />
            <select value={form.plan_interested} onChange={set("plan_interested", setForm)} data-testid="ref-plan-select" className="input-dark text-sm w-full" required>
              <option value="">Plan Interested *</option>
              {plans.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
            <textarea value={form.note} onChange={set("note", setForm)} rows={2} placeholder="Note for admin (optional)" data-testid="ref-note" className="input-dark text-sm w-full resize-none" />
            {error && <p className="text-xs text-red-400" data-testid="referral-error">{error}</p>}
            {notice && <p className="text-xs text-emerald-400" data-testid="referral-notice">{notice}</p>}
            <button type="submit" disabled={busy} data-testid="referral-submit-btn" className="btn-gold w-full !py-3 text-sm disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <><Send size={15} /> SUBMIT REFERRAL</>}
            </button>
          </form>

          <div className="card-surface p-6 lg:col-span-3 overflow-x-auto" data-testid="referrals-table-card">
            <h2 className="font-display text-lg font-bold text-slate-100 mb-4">My Referrals</h2>
            {!data ? <Loader2 size={18} className="animate-spin text-gold mx-auto my-8" /> : data.referrals.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="no-referrals">No referrals yet — submit your first client.</p>
            ) : (
              <table className="w-full min-w-[560px]" data-testid="referrals-table">
                <thead><tr className="border-b border-slate-800">
                  {["Client", "Company", "Plan", "Status", ""].map((h) => <th key={h} className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 py-2">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.referrals.map((r) => (
                    <tr key={r.referral_id} className="border-b border-slate-800/60" data-testid={`referral-row-${r.referral_id}`}>
                      <td className="py-3 pr-3">
                        <p className="text-sm text-slate-200">{r.client_name}</p>
                        <p className="text-[10px] text-slate-500">{r.client_email}</p>
                      </td>
                      <td className="py-3 pr-3 text-xs text-slate-400">{r.company}</td>
                      <td className="py-3 pr-3 text-xs text-slate-400">{r.plan_interested}</td>
                      <td className="py-3 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-mono ${STATUS_STYLE[r.status]}`} data-testid={`status-${r.referral_id}`}>{r.status}</span>
                        {r.status === "REJECTED" && r.admin_note && <p className="text-[10px] text-red-400/80 mt-1 max-w-[180px]" data-testid={`note-${r.referral_id}`}>{r.admin_note}</p>}
                      </td>
                      <td className="py-3 text-right">
                        {r.status === "REJECTED" && (
                          <button onClick={() => { setEditTarget(r); setEditForm({ client_name: r.client_name, client_email: r.client_email, client_phone: r.client_phone || "", company: r.company, plan_interested: r.plan_interested, note: r.note || "" }); setError(""); }}
                            data-testid={`resubmit-${r.referral_id}`} className="btn-outline !px-2.5 !py-1 text-[10px]"><RefreshCw size={11} /> EDIT & RESUBMIT</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {editTarget && (
        <div className="fixed inset-0 z-50 bg-navy-950/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setEditTarget(null)} data-testid="resubmit-modal">
          <form onSubmit={resubmit} onClick={(e) => e.stopPropagation()} className="card-surface p-6 w-full max-w-md space-y-4">
            <h3 className="font-display text-lg font-bold text-slate-100">Update & Resubmit</h3>
            <p className="text-[11px] text-slate-500">Referral {editTarget.referral_id} — fix the details and send it back for review.</p>
            <input value={editForm.client_name} onChange={set("client_name", setEditForm)} placeholder="Client Name *" data-testid="edit-client-name" className="input-dark text-sm w-full" required />
            <input type="email" value={editForm.client_email} onChange={set("client_email", setEditForm)} placeholder="Client Email *" data-testid="edit-client-email" className="input-dark text-sm w-full" required />
            <input value={editForm.client_phone} onChange={set("client_phone", setEditForm)} placeholder="Client Phone" data-testid="edit-client-phone" className="input-dark text-sm w-full" />
            <input value={editForm.company} onChange={set("company", setEditForm)} placeholder="Client Company *" data-testid="edit-company" className="input-dark text-sm w-full" required />
            <select value={editForm.plan_interested} onChange={set("plan_interested", setEditForm)} data-testid="edit-plan-select" className="input-dark text-sm w-full" required>
              <option value="">Plan Interested *</option>
              {plans.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
            <textarea value={editForm.note} onChange={set("note", setEditForm)} rows={2} placeholder="Note for admin (optional)" data-testid="edit-note" className="input-dark text-sm w-full resize-none" />
            {error && <p className="text-xs text-red-400" data-testid="resubmit-error">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={busy} data-testid="resubmit-confirm-btn" className="btn-gold flex-1 !py-2.5 text-xs disabled:opacity-60">{busy ? "SAVING…" : "RESUBMIT FOR REVIEW"}</button>
              <button type="button" onClick={() => setEditTarget(null)} data-testid="resubmit-cancel-btn" className="btn-outline !py-2.5 text-xs">CANCEL</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
